import { createContext, useContext } from 'react';
import ReactDOM from 'react-dom/client';
import { MapProvider } from "react-map-gl";
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import MaterialThemeProvider from './components/MaterialThemeProvider';
import MobileFlag from './components/MobileFlag';
import './index.css';
import AuthService from './services/authService';
import DealFieldsProvider from './services/dealFieldsProvider';
import DealPortfolioApiService from './services/dealPortfolioApiService';
import FavoriteApiService from "./services/favoriteApiService";
import GeometryService from "./services/geometryService";
import IdentityService from './services/identityService';
import LeadsApiService from './services/leadsApiService';
import LeaseCompsApiService from './services/leaseCompsApiService';
import LocalStorageService from './services/localStorageService';
import LocationsApiService from "./services/locationsApiService";
import MapboxApiService from "./services/mapboxApiService";
import ParcelApiService from "./services/parcelApiService";
import PipedriveApiService from './services/pipedriveApiService';
import SaleCompsApiService from './services/saleCompsApiService';
import SavedViewsApiService from './services/savedViewsApiService';
import TenantsInMarketApiService from './services/tenantsInMarketApiService';
import ZoningFiltersApiService from './services/zoningFiltersApiService';
import AddLabelStore from './store/AddLabelStore';
import AuthStore from './store/AuthStore';
import ChangeLeadStatusStore from './store/ChangeLeadStatusStore';
import ClosableStore from './store/ClosableStore';
import DealPortfolioStore from './store/DealPortfolioStore';
import ContextMenuStore from './store/ContextMenuStore';
import DetailsStore from './store/DetailsStore';
import DrawMapStore from './store/DrawMapStore';
import DriveTimeStore from './store/DriveTimeStore';
import FavoriteStore from "./store/FavoriteStore";
import FilterSettingsStore from './store/FilterSettingsStore';
import LeadStore from './store/LeadStore';
import LeaseCompStore from './store/LeaseCompStore';
import ListStore from './store/ListStore';
import LocationStore from "./store/LocationStore";
import MapMarkerStore from './store/MapMarkerStore';
import MapRadiusStore from './store/MapRadiusStore';
import MapShapeStore from './store/MapShapeStore';
import MapStore from './store/MapStore';
import PipedriveStore from './store/PipedriveStore';
import PopupStore from './store/PopupStore';
import PropertyAiGradeStore from './store/PropertyAiGradeStore';
import PropertyContactInfoStore from './store/PropertyContactInfoStore';
import PropertyStore from './store/PropertyStore';
import RecentStore from './store/RecentStore';
import RecordedSaleStore from './store/RecordedSaleStore';
import SaleCompStore from './store/SaleCompStore';
import SavedViewStore from './store/SavedViewStore';
import SearchStore from './store/SearchStore';
import SegmentedButtonsStore from './store/SegmentedButtonsStore';
import SetReminderStore from './store/SetReminderStore';
import StreetViewStore from './store/StreetViewStore';
import TenantInMarketStore from './store/TenantInMarketStore';
import ViewSettingsStore from './store/ViewSettingsStore';

const localStorageService = new LocalStorageService();
const favoriteApiService = new FavoriteApiService();
const geometryService = new GeometryService();
const parcelApiService = new ParcelApiService();
const mapboxApiService = new MapboxApiService();

const authStore = new AuthStore(
    localStorageService,
    new AuthService(),
    new IdentityService(),
);

const AuthContext = createContext<AuthStore>(authStore);
export const useAuthStore = () => useContext(AuthContext);

const mapStore = new MapStore(localStorageService, new ZoningFiltersApiService());
const MapContext = createContext<MapStore>(mapStore);
export const useMapStore = () => useContext(MapContext);

const leadStore = new LeadStore(new LeadsApiService());
const LeadContext = createContext<LeadStore>(leadStore);
export const useLeadStore = () => useContext(LeadContext);

const leaseCompStore = new LeaseCompStore(new LeaseCompsApiService());
const LeaseCompContext = createContext<LeaseCompStore>(leaseCompStore);
export const useLeaseCompStore = () => useContext(LeaseCompContext);

const saleCompStore = new SaleCompStore(new SaleCompsApiService());
const SaleCompContext = createContext<SaleCompStore>(saleCompStore);
export const useSaleCompStore = () => useContext(SaleCompContext);

const tenantInMarketStore = new TenantInMarketStore(new TenantsInMarketApiService());
const TenantInMarketContext = createContext<TenantInMarketStore>(tenantInMarketStore);
export const useTenantInMarketStore = () => useContext(TenantInMarketContext);

const dealFieldsProvider = new DealFieldsProvider();
const pipedriveStore = new PipedriveStore(
    new PipedriveApiService(dealFieldsProvider),
    localStorageService,
    dealFieldsProvider,
);

const PipedriveContext = createContext<PipedriveStore>(pipedriveStore);
export const usePipedriveStore = () => useContext(PipedriveContext);

const searchStore = new SearchStore();
const SearchContext = createContext<SearchStore>(searchStore);
export const useSearchStore = () => useContext(SearchContext);

const recentStore = new RecentStore(localStorageService);
const RecentContext = createContext<RecentStore>(recentStore);
export const useRecentStore = () => useContext(RecentContext);

const propertyStore = new PropertyStore(parcelApiService, mapboxApiService, geometryService);
const PropertyContext = createContext<PropertyStore>(propertyStore);
export const usePropertyStore = () => useContext(PropertyContext);

const propertyAiGradeStore = new PropertyAiGradeStore();
const PropertyAiGradeContext = createContext<PropertyAiGradeStore>(propertyAiGradeStore);
export const usePropertyAiGradeStore = () => useContext(PropertyAiGradeContext);

const propertyContactInfoStore = new PropertyContactInfoStore();
const PropertyContactInfoContext = createContext<PropertyContactInfoStore>(propertyContactInfoStore);
export const usePropertyContactInfoStore = () => useContext(PropertyContactInfoContext);

const filterSettingsStore = new FilterSettingsStore(localStorageService);
const FilterSettingsContext = createContext<FilterSettingsStore>(filterSettingsStore);
export const useFilterSettingsStore = () => useContext(FilterSettingsContext);

const drawMapStore = new DrawMapStore(geometryService);
const DrawMapContext = createContext<DrawMapStore>(drawMapStore);
export const useDrawMapStore = () => useContext(DrawMapContext);

const favoritesStore = new FavoriteStore(favoriteApiService);
const FavoriteContext = createContext<FavoriteStore>(favoritesStore);
export const useFavoriteStore = () => useContext(FavoriteContext);

const contextMenuStore = new ContextMenuStore();
const ContextMenuContext = createContext<ContextMenuStore>(contextMenuStore);
export const useContextMenuStore = () => useContext(ContextMenuContext);

const mapMarkerStore = new MapMarkerStore();
const MapMarkerContext = createContext<MapMarkerStore>(mapMarkerStore);
export const useMapMarkerStore = () => useContext(MapMarkerContext);

const driveTimeStore = new DriveTimeStore(mapboxApiService, parcelApiService);
const DriveTimeContext = createContext<DriveTimeStore>(driveTimeStore);
export const useDriveTimeStore = () => useContext(DriveTimeContext);

const streetViewStore = new StreetViewStore();
const StreetViewContext = createContext<StreetViewStore>(streetViewStore);
export const useStreetViewStore = () => useContext(StreetViewContext);

const locationStore = new LocationStore(new LocationsApiService());
const LocationContext = createContext<LocationStore>(locationStore);
export const useLocationStore = () => useContext(LocationContext);

const mapRadiusStore = new MapRadiusStore();
const MapRadiusContext = createContext<MapRadiusStore>(mapRadiusStore);
export const useMapRadiusStore = () => useContext(MapRadiusContext);

const mapShapeStore = new MapShapeStore();
const MapShapeContext = createContext<MapShapeStore>(mapShapeStore);
export const useMapShapeStore = () => useContext(MapShapeContext);

const recordedSaleStore = new RecordedSaleStore();
const RecordedSaleContext = createContext<RecordedSaleStore>(recordedSaleStore);
export const useRecordedSaleStore = () => useContext(RecordedSaleContext);

const popupStore = new PopupStore();
const PopupContext = createContext<PopupStore>(popupStore);
export const usePopupStore = () => useContext(PopupContext);

const listStore = new ListStore();
const ListContext = createContext<ListStore>(listStore);
export const useListStore = () => useContext(ListContext);

const detailsStore = new DetailsStore();
const DetailsContext = createContext<DetailsStore>(detailsStore);
export const useDetailsStore = () => useContext(DetailsContext);

const viewSettingsStore = new ViewSettingsStore();
const ViewSettingsContext = createContext<ViewSettingsStore>(viewSettingsStore);
export const useViewSettingsStore = () => useContext(ViewSettingsContext);

const savedViewStore = new SavedViewStore(new SavedViewsApiService(), mapStore, locationStore, filterSettingsStore, drawMapStore);
const SavedViewContext = createContext<SavedViewStore>(savedViewStore);
export const useSavedViewStore = () => useContext(SavedViewContext);

const closableStore = new ClosableStore();
const ClosableContext = createContext<ClosableStore>(closableStore);
export const useClosableStore = () => useContext(ClosableContext);

const segmentedButtonsStore = new SegmentedButtonsStore();
const SegmentedButtonsContext = createContext<SegmentedButtonsStore>(segmentedButtonsStore);
export const useSegmentedButtonsStore = () => useContext(SegmentedButtonsContext);

const setReminderStore = new SetReminderStore();
const SetReminderContext = createContext<SetReminderStore>(setReminderStore);
export const useSetReminderStore = () => useContext(SetReminderContext);

const dealPortfolioStore = new DealPortfolioStore(new DealPortfolioApiService());
const DealPortfolioContext = createContext<DealPortfolioStore>(dealPortfolioStore);
export const useDealPortfolioStore = () => useContext(DealPortfolioContext);

const addLabelStore = new AddLabelStore();
const AddLabelContext = createContext<AddLabelStore>(addLabelStore);
export const useAddLabelStore = () => useContext(AddLabelContext);

const changeLeadStatusStore = new ChangeLeadStatusStore();
const ChangeLeadStatusContext = createContext<ChangeLeadStatusStore>(changeLeadStatusStore);
export const useChangeLeadStatusStore = () => useContext(ChangeLeadStatusContext);

ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement
).render(
    <MapProvider>
        <BrowserRouter basename={process.env.PUBLIC_URL}>
            <MaterialThemeProvider>
                <MobileFlag>
                    <AuthContext.Provider value={authStore}>
                        <MapContext.Provider value={mapStore}>
                            <LeadContext.Provider value={leadStore}>
                                <LeaseCompContext.Provider value={leaseCompStore}>
                                    <SaleCompContext.Provider value={saleCompStore}>
                                        <TenantInMarketContext.Provider value={tenantInMarketStore}>
                                            <PipedriveContext.Provider value={pipedriveStore}>
                                                <SearchContext.Provider value={searchStore}>
                                                    <RecentContext.Provider value={recentStore}>
                                                        <PropertyContext.Provider value={propertyStore}>
                                                            <PropertyAiGradeContext.Provider value={propertyAiGradeStore}>
                                                                <FilterSettingsContext.Provider value={filterSettingsStore}>
                                                                    <DrawMapContext.Provider value={drawMapStore}>
                                                                        <FavoriteContext.Provider value={favoritesStore}>
                                                                            <ContextMenuContext.Provider value={contextMenuStore}>
                                                                                <MapMarkerContext.Provider value={mapMarkerStore}>
                                                                                    <DriveTimeContext.Provider value={driveTimeStore}>
                                                                                        <StreetViewContext.Provider value={streetViewStore}>
                                                                                            <LocationContext.Provider value={locationStore}>
                                                                                                <MapRadiusContext.Provider value={mapRadiusStore}>
                                                                                                    <MapShapeContext.Provider value={mapShapeStore}>
                                                                                                        <RecordedSaleContext.Provider value={recordedSaleStore}>
                                                                                                            <PopupContext.Provider value={popupStore}>
                                                                                                                <ListContext.Provider value={listStore}>
                                                                                                                    <DetailsContext.Provider value={detailsStore}>
                                                                                                                        <ViewSettingsContext.Provider value={viewSettingsStore}>
                                                                                                                            <SavedViewContext.Provider value={savedViewStore}>
                                                                                                                                <ClosableContext.Provider value={closableStore}>
                                                                                                                                    <SegmentedButtonsContext.Provider value={segmentedButtonsStore}>
                                                                                                                                        <SetReminderContext.Provider value={setReminderStore}>
                                                                                                                                            <DealPortfolioContext.Provider value={dealPortfolioStore}>
                                                                                                                                                <AddLabelContext.Provider value={addLabelStore}>
                                                                                                                                                    <ChangeLeadStatusContext.Provider value={changeLeadStatusStore}>
                                                                                                                                                        <App />
                                                                                                                                                    </ChangeLeadStatusContext.Provider>
                                                                                                                                                </AddLabelContext.Provider>
                                                                                                                                            </DealPortfolioContext.Provider>
                                                                                                                                        </SetReminderContext.Provider>
                                                                                                                                    </SegmentedButtonsContext.Provider>
                                                                                                                                </ClosableContext.Provider>
                                                                                                                            </SavedViewContext.Provider>
                                                                                                                        </ViewSettingsContext.Provider>
                                                                                                                    </DetailsContext.Provider>
                                                                                                                </ListContext.Provider>
                                                                                                            </PopupContext.Provider>
                                                                                                        </RecordedSaleContext.Provider>
                                                                                                    </MapShapeContext.Provider>
                                                                                                </MapRadiusContext.Provider>
                                                                                            </LocationContext.Provider>
                                                                                        </StreetViewContext.Provider>
                                                                                    </DriveTimeContext.Provider>
                                                                                </MapMarkerContext.Provider>
                                                                            </ContextMenuContext.Provider>
                                                                        </FavoriteContext.Provider>
                                                                    </DrawMapContext.Provider>
                                                                </FilterSettingsContext.Provider>
                                                            </PropertyAiGradeContext.Provider>
                                                        </PropertyContext.Provider>
                                                    </RecentContext.Provider>
                                                </SearchContext.Provider>
                                            </PipedriveContext.Provider>
                                        </TenantInMarketContext.Provider>
                                    </SaleCompContext.Provider>
                                </LeaseCompContext.Provider>
                            </LeadContext.Provider>
                        </MapContext.Provider>
                    </AuthContext.Provider>
                </MobileFlag>
            </MaterialThemeProvider>
        </BrowserRouter>
    </MapProvider>
);
